import { useCallback } from 'react';
import { Strategy } from 'client/voladeum/strategies/types';
import { selectAdapters } from 'ducks/adapters/selectors';
import { selectModal } from 'ducks/modals/selectors';
import { clearModal } from 'ducks/modals/slices';
import { selectPairs } from 'ducks/pairs/selectors';
import { selectSizingMethods } from 'ducks/sizing-methods/selectors';
import { selectStrategy } from 'ducks/strategy/selectors';
import { Button } from 'framework/button';
import { Dropdown, DropdownCalendar } from 'framework/dropdown';
import { Input, InputNumber } from 'framework/Input';
import { Modal } from 'framework/modal';
import moment from 'moment';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { useStrategyApi } from 'pages/hooks/useStrategyApi';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { SizingMethodInput } from '../components/SizingMethodInput';

import cn from '../Modal.module.scss';

export const ManageStrategyModal = () => {
  const dispatch = useAppDispatch();
  const voladeum_strategy = useAppSelector(selectStrategy);
  const voladeum_pairs = useAppSelector(selectPairs);
  const voladeum_adapters = useAppSelector(selectAdapters);
  const voladeum_sizing_methods = useAppSelector(selectSizingMethods);
  const { is_open, is_update } = useAppSelector(selectModal('strategy_manage'));
  const { strategy, setStrategy, updateStrategy } = useStrategyContext();
  const { onStrategySubmit } = useStrategyApi();

  const onClose = useCallback(() => {
    setStrategy(voladeum_strategy);
    dispatch(clearModal('strategy_manage'));
  }, [dispatch, setStrategy, voladeum_strategy]);

  const onUpdate = useCallback(
    (entity: Partial<Strategy>) => updateStrategy(entity),
    [updateStrategy]
  );

  const {
    strategy_name,
    adapter_name,
    adapter_id,
    pair_name,
    sizing_method_id,
    sizing_method_name,
    max_daily_loss_pct_formatted,
    max_position_pct_formatted,
    activation_threshold_formatted,
    to_utc_formatted,
  } = strategy;

  return (
    <Modal
      is_open={is_open}
      identifier="strategy_manage"
      width="450px"
      onSubmit={onStrategySubmit}
      onClose={onClose}
    >
      <Modal.Header onClose={onClose} dismiss>
        {is_update ? 'Update Strategy' : 'Create Strategy'}
      </Modal.Header>
      <Modal.Body>
        <Input
          label="Strategy Name *"
          value={strategy_name}
          onChange={({ value }) => {
            onUpdate({ strategy_name: value });
          }}
        />

        <div className={cn.row}>
          <Dropdown
            label="Provider *"
            value={adapter_name}
            options={voladeum_adapters}
            option_key="adapter_name"
            disabled={is_update}
            onSelect={({ adapter_id, adapter_name }) =>
              onUpdate({ adapter_id, adapter_name, pair_id: 0, pair_name: '' })
            }
          />
          <Dropdown
            label="Pair *"
            options={voladeum_pairs.filter((pair) => pair.adapter_id === adapter_id)}
            option_key="pair_name"
            value={pair_name}
            disabled={!adapter_id || is_update}
            onSelect={({ pair_id, pair_name }) => onUpdate({ pair_id, pair_name })}
          />
        </div>
        <DropdownCalendar
          label="End Date (Backtest) *"
          information="Lookback 1 day when building the strategy and 30 days when backtesting."
          value={[to_utc_formatted]}
          onSelect={([date]) => {
            const since_milliseconds = moment.utc(date, 'MM/DD/YYYY').startOf('day').valueOf();
            const to_milliseconds = moment.utc(date, 'MM/DD/YYYY').endOf('day').valueOf();
            onUpdate({
              since_utc_formatted: date,
              since_milliseconds: since_milliseconds,
              to_utc_formatted: date,
              to_milliseconds: to_milliseconds,
            });
          }}
        />
      </Modal.Body>
      <Modal.Additional>
        <Dropdown
          label="Sizing Method *"
          options={voladeum_sizing_methods}
          option_key="sizing_method_name"
          value={sizing_method_name}
          onSelect={({ sizing_method_id, sizing_method_name }) =>
            onUpdate({
              sizing_method_id,
              sizing_method_name,
              max_position_pct: 0,
              max_position_pct_formatted: '0',
              max_daily_loss_pct: 0,
              max_daily_loss_pct_formatted: '0',
            })
          }
        />
        <div className={cn.row}>
          <SizingMethodInput
            label="Max Position *"
            value={max_position_pct_formatted}
            sizing_method_id={sizing_method_id}
            onChange={({ float_value, formatted_value }) => {
              onUpdate({
                max_position_pct: float_value,
                max_position_pct_formatted: formatted_value,
              });
            }}
          />
          <SizingMethodInput
            label="Max Daily Loss *"
            value={max_daily_loss_pct_formatted}
            sizing_method_id={sizing_method_id}
            onChange={({ float_value, formatted_value }) => {
              onUpdate({
                max_daily_loss_pct: float_value,
                max_daily_loss_pct_formatted: formatted_value,
              });
            }}
          />
        </div>
        <InputNumber
          label="Threshold *"
          information="minimum threshold to trigger an order"
          value={activation_threshold_formatted}
          onChange={({ float_value, formatted_value }) =>
            onUpdate({
              activation_threshold: float_value,
              activation_threshold_formatted: formatted_value,
            })
          }
        />
      </Modal.Additional>
      <Modal.Footer>
        <div className={cn.flex} />
        <Button type="submit">Ok</Button>
      </Modal.Footer>
    </Modal>
  );
};
